import { useState } from "react";
import Button from "./Button";
import {
  INDICATOR_DEFAULTS,
  defaultLabelFor,
  makeNewIndicatorInstance,
  type IndicatorInstance,
  type IndicatorType,
} from "../lib/indicators";

const INDICATOR_OPTIONS: { value: IndicatorType; label: string }[] = [
  { value: "sma", label: "SMA — Simple MA" },
  { value: "ema", label: "EMA — Exponential MA" },
  { value: "wma", label: "WMA — Weighted MA" },
  { value: "bollinger", label: "Bollinger Bands" },
  { value: "donchian", label: "Donchian Channels" },
  { value: "rsi", label: "RSI" },
  { value: "macd", label: "MACD" },
  { value: "stochastic", label: "Stochastic" },
  { value: "atr", label: "ATR" },
  { value: "roc", label: "ROC — Rate of Change" },
  { value: "supertrend", label: "Supertrend" },
  { value: "vwap", label: "VWAP (session)" },
];

export default function IndicatorsPanel({
  indicators,
  onAdd,
  onRemove,
  onEdit,
}: {
  indicators: IndicatorInstance[];
  onAdd: (inst: IndicatorInstance) => void;
  onRemove: (id: string) => void;
  onEdit?: (inst: IndicatorInstance) => void;
}) {
  const [adding, setAdding] = useState(false);
  return (
    <div className="flex flex-wrap items-center gap-2 mb-3">
      <span className="text-xs text-muted uppercase tracking-wide">
        Indicators:
      </span>
      {indicators.length === 0 && (
        <span className="text-xs text-muted">none</span>
      )}
      {indicators.map((i) => (
        <span
          key={i.id}
          className="inline-flex items-center gap-1 text-xs font-mono bg-surface-2 border border-border rounded px-2 py-0.5"
          style={i.color ? { borderColor: i.color } : undefined}
        >
          <span
            className="inline-block w-2 h-2 rounded-full"
            style={{ background: i.color ?? "#0ea5e9" }}
          />
          <button
            type="button"
            onClick={() => onEdit?.(i)}
            className="hover:underline disabled:cursor-default disabled:no-underline"
            disabled={!onEdit}
            title={onEdit ? "Edit params + color + line width" : undefined}
          >
            {i.label}
          </button>
          <button
            type="button"
            onClick={() => onRemove(i.id)}
            className="ml-1 text-muted hover:text-down-600 dark:text-down-400"
            aria-label="Remove indicator"
          >
            ×
          </button>
        </span>
      ))}
      {adding ? (
        <AddIndicatorForm
          onCancel={() => setAdding(false)}
          onAdd={(inst) => {
            onAdd(inst);
            setAdding(false);
          }}
        />
      ) : (
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={() => setAdding(true)}
        >
          + Add indicator
        </Button>
      )}
    </div>
  );
}

function AddIndicatorForm({
  onCancel,
  onAdd,
}: {
  onCancel: () => void;
  onAdd: (inst: IndicatorInstance) => void;
}) {
  const [type, setType] = useState<IndicatorType>("sma");
  const [params, setParams] = useState<Record<string, number>>({
    ...INDICATOR_DEFAULTS.sma.params,
  });

  function onTypeChange(next: IndicatorType) {
    setType(next);
    setParams({ ...INDICATOR_DEFAULTS[next].params });
  }

  function submit() {
    onAdd(makeNewIndicatorInstance(type, params));
  }

  return (
    <div className="inline-flex items-center gap-2 border border-border rounded px-2 py-1 bg-surface">
      <select
        value={type}
        onChange={(e) => onTypeChange(e.target.value as IndicatorType)}
        className="text-xs border border-border rounded px-1 py-0.5"
      >
        {INDICATOR_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {Object.entries(params).map(([k, v]) => (
        <label key={k} className="text-xs text-fg-subtle flex items-center gap-1">
          {k}
          <input
            type="number"
            value={v}
            min={k === "multiplier" ? 0.1 : 1}
            step={k === "std_dev" || k === "multiplier" ? 0.1 : 1}
            onChange={(e) =>
              setParams((cur) => ({
                ...cur,
                [k]: Number(e.target.value),
              }))
            }
            className="w-16 text-xs border border-border rounded px-1 py-0.5 font-mono"
          />
        </label>
      ))}
      <span className="text-xs text-muted font-mono">
        {defaultLabelFor(type, params)}
      </span>
      <Button type="button" size="sm" onClick={submit}>
        Add
      </Button>
      <Button type="button" size="sm" variant="secondary" onClick={onCancel}>
        Cancel
      </Button>
    </div>
  );
}
